import Papa from 'papaparse';
import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import { ImageProcessor } from '../image/ImageProcessor';
import type { ConversionCategory, ConversionProfile, ImageOutputFormat, ProcessedFile } from '../../types';

export type ConversionProfileId = string;
export interface ConversionOptions {
  compression: number;
}

export interface SizeEstimate {
  bytes: number;
  confidence: 'low' | 'medium' | 'high';
}

type BrowserImageFormat = Extract<ImageOutputFormat, 'image/png' | 'image/jpeg' | 'image/webp' | 'image/avif'>;
type ConversionStrategy = 'canvas-image' | 'ffmpeg-media' | 'raster-svg' | 'json-csv' | 'csv-json' | 'zip' | 'docx-text' | 'text-pdf' | 'text-copy' | 'server';

interface ProfileDefinition extends ConversionProfile {
  strategy: ConversionStrategy;
  canvasFormat?: BrowserImageFormat;
  ffmpegArgs?: (inputName: string, outputName: string, options: ConversionOptions) => string[];
}

const imageInputAccept = [
  'image/png',
  'image/jpeg',
  'image/avif',
  'image/webp',
  'image/bmp',
  'image/gif',
  'image/svg+xml',
  '.bmp',
  '.eps',
  '.gif',
  '.ico',
  '.jpeg',
  '.jpg',
  '.odd',
  '.png',
  '.psd',
  '.svg',
  '.tga',
  '.tif',
  '.tiff',
  '.webp',
].join(',');

const audioInputAccept = [
  'audio/*',
  '.aac',
  '.aiff',
  '.alac',
  '.flac',
  '.m4a',
  '.mp3',
  '.oga',
  '.ogg',
  '.opus',
  '.wav',
  '.wma',
].join(',');

const videoInputAccept = ['video/*', '.avi', '.gif', '.mov', '.mkv', '.mp4', '.mpeg', '.mpg', '.webm'].join(',');
const textInputAccept = '.txt,.text,text/plain';

const passthroughAudioArgs = (codecArgs: (options: ConversionOptions) => string[]) => (inputName: string, outputName: string, options: ConversionOptions) => [
  '-i',
  inputName,
  '-vn',
  ...codecArgs(options),
  outputName,
];

const passthroughImageArgs = (inputName: string, outputName: string) => ['-i', inputName, outputName];

const profiles: ProfileDefinition[] = [
  {
    id: 'image-to-png',
    title: 'imageToPng',
    description: 'imageToPngDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'png',
    outputMimeType: 'image/png',
    browserSupported: true,
    strategy: 'canvas-image',
    canvasFormat: 'image/png',
  },
  {
    id: 'image-to-jpeg',
    title: 'imageToJpeg',
    description: 'imageToJpegDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'jpeg',
    outputMimeType: 'image/jpeg',
    browserSupported: true,
    strategy: 'canvas-image',
    canvasFormat: 'image/jpeg',
  },
  {
    id: 'image-to-jpg',
    title: 'imageToJpg',
    description: 'imageToJpgDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'jpg',
    outputMimeType: 'image/jpeg',
    browserSupported: true,
    strategy: 'canvas-image',
    canvasFormat: 'image/jpeg',
  },
  {
    id: 'image-to-avif',
    title: 'imageToAvif',
    description: 'imageToAvifDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'avif',
    outputMimeType: 'image/avif',
    browserSupported: true,
    strategy: 'canvas-image',
    canvasFormat: 'image/avif',
  },
  {
    id: 'image-to-webp',
    title: 'imageToWebp',
    description: 'imageToWebpDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'webp',
    outputMimeType: 'image/webp',
    browserSupported: true,
    strategy: 'canvas-image',
    canvasFormat: 'image/webp',
  },
  {
    id: 'image-to-bmp',
    title: 'imageToBmp',
    description: 'imageToBmpDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'bmp',
    outputMimeType: 'image/bmp',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughImageArgs,
  },
  {
    id: 'image-to-gif',
    title: 'imageToGif',
    description: 'imageToGifDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'gif',
    outputMimeType: 'image/gif',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughImageArgs,
  },
  {
    id: 'image-to-ico',
    title: 'imageToIco',
    description: 'imageToIcoDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'ico',
    outputMimeType: 'image/x-icon',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughImageArgs,
  },
  {
    id: 'image-to-svg',
    title: 'imageToSvg',
    description: 'imageToSvgDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'svg',
    outputMimeType: 'image/svg+xml',
    browserSupported: true,
    strategy: 'raster-svg',
  },
  {
    id: 'image-to-tga',
    title: 'imageToTga',
    description: 'imageToTgaDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'tga',
    outputMimeType: 'image/x-tga',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughImageArgs,
  },
  {
    id: 'image-to-tiff',
    title: 'imageToTiff',
    description: 'imageToTiffDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'tiff',
    outputMimeType: 'image/tiff',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughImageArgs,
  },
  {
    id: 'image-to-eps',
    title: 'imageToEps',
    description: 'serverRequiredDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'eps',
    outputMimeType: 'application/postscript',
    browserSupported: false,
    requiresServer: true,
    strategy: 'server',
  },
  {
    id: 'image-to-psd',
    title: 'imageToPsd',
    description: 'serverRequiredDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'psd',
    outputMimeType: 'image/vnd.adobe.photoshop',
    browserSupported: false,
    requiresServer: true,
    strategy: 'server',
  },
  {
    id: 'image-to-odd',
    title: 'imageToOdd',
    description: 'serverRequiredDesc',
    accept: imageInputAccept,
    category: 'image',
    outputExtension: 'odd',
    outputMimeType: 'application/octet-stream',
    browserSupported: false,
    requiresServer: true,
    strategy: 'server',
  },
  {
    id: 'audio-to-mp3',
    title: 'audioToMp3',
    description: 'audioToMp3Desc',
    accept: audioInputAccept,
    category: 'audio',
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughAudioArgs((options) => ['-codec:a', 'libmp3lame', '-b:a', `${getAudioBitrate(options)}k`]),
  },
  {
    id: 'audio-to-wav',
    title: 'audioToWav',
    description: 'audioToWavDesc',
    accept: audioInputAccept,
    category: 'audio',
    outputExtension: 'wav',
    outputMimeType: 'audio/wav',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughAudioArgs(() => ['-codec:a', 'pcm_s16le']),
  },
  {
    id: 'audio-to-ogg',
    title: 'audioToOgg',
    description: 'audioToOggDesc',
    accept: audioInputAccept,
    category: 'audio',
    outputExtension: 'ogg',
    outputMimeType: 'audio/ogg',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughAudioArgs((options) => ['-codec:a', 'libopus', '-b:a', `${getAudioBitrate(options)}k`]),
  },
  {
    id: 'audio-to-flac',
    title: 'audioToFlac',
    description: 'audioToFlacDesc',
    accept: audioInputAccept,
    category: 'audio',
    outputExtension: 'flac',
    outputMimeType: 'audio/flac',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughAudioArgs(() => ['-codec:a', 'flac']),
  },
  {
    id: 'audio-to-aac',
    title: 'audioToAac',
    description: 'audioToAacDesc',
    accept: audioInputAccept,
    category: 'audio',
    outputExtension: 'aac',
    outputMimeType: 'audio/aac',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughAudioArgs((options) => ['-codec:a', 'aac', '-b:a', `${getAudioBitrate(options)}k`]),
  },
  {
    id: 'audio-to-m4a',
    title: 'audioToM4a',
    description: 'audioToM4aDesc',
    accept: audioInputAccept,
    category: 'audio',
    outputExtension: 'm4a',
    outputMimeType: 'audio/mp4',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: passthroughAudioArgs((options) => ['-codec:a', 'aac', '-b:a', `${getAudioBitrate(options)}k`]),
  },
  {
    id: 'video-to-mp4',
    title: 'videoToMp4',
    description: 'videoToMp4Desc',
    accept: videoInputAccept,
    category: 'video',
    outputExtension: 'mp4',
    outputMimeType: 'video/mp4',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: (inputName, outputName, options) => [
      '-i',
      inputName,
      '-c:v',
      'libx264',
      '-preset',
      'veryfast',
      '-crf',
      String(getVideoCrf(options)),
      '-c:a',
      'aac',
      '-b:a',
      `${getAudioBitrate(options)}k`,
      outputName,
    ],
  },
  {
    id: 'video-to-webm',
    title: 'videoToWebm',
    description: 'videoToWebmDesc',
    accept: videoInputAccept,
    category: 'video',
    outputExtension: 'webm',
    outputMimeType: 'video/webm',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: (inputName, outputName, options) => ['-i', inputName, '-c:v', 'libvpx-vp9', '-b:v', `${getVideoBitrate(options)}k`, '-c:a', 'libopus', outputName],
  },
  {
    id: 'video-to-gif',
    title: 'videoToGif',
    description: 'videoToGifDesc',
    accept: videoInputAccept,
    category: 'video',
    outputExtension: 'gif',
    outputMimeType: 'image/gif',
    browserSupported: true,
    strategy: 'ffmpeg-media',
    ffmpegArgs: (inputName, outputName, options) => [
      '-i',
      inputName,
      '-vf',
      `fps=${getGifFps(options)},scale=${getGifWidth(options)}:-1:flags=lanczos`,
      '-loop',
      '0',
      outputName,
    ],
  },
  {
    id: 'docx-to-txt',
    title: 'docxToTxt',
    description: 'docxToTxtDesc',
    accept: '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    category: 'document',
    outputExtension: 'txt',
    outputMimeType: 'text/plain;charset=utf-8',
    browserSupported: true,
    strategy: 'docx-text',
  },
  {
    id: 'txt-to-pdf',
    title: 'txtToPdf',
    description: 'txtToPdfDesc',
    accept: textInputAccept,
    category: 'document',
    outputExtension: 'pdf',
    outputMimeType: 'application/pdf',
    browserSupported: true,
    strategy: 'text-pdf',
  },
  {
    id: 'txt-to-text',
    title: 'txtToText',
    description: 'txtToTextDesc',
    accept: textInputAccept,
    category: 'document',
    outputExtension: 'text',
    outputMimeType: 'text/plain;charset=utf-8',
    browserSupported: true,
    strategy: 'text-copy',
  },
  {
    id: 'document-to-doc',
    title: 'documentToDoc',
    description: 'serverRequiredDesc',
    accept: '.doc,.docx,.pdf,.ps,.txt,.text,.word',
    category: 'document',
    outputExtension: 'doc',
    outputMimeType: 'application/msword',
    browserSupported: false,
    requiresServer: true,
    strategy: 'server',
  },
  {
    id: 'document-to-docx',
    title: 'documentToDocx',
    description: 'serverRequiredDesc',
    accept: '.doc,.docx,.pdf,.ps,.txt,.text,.word',
    category: 'document',
    outputExtension: 'docx',
    outputMimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    browserSupported: false,
    requiresServer: true,
    strategy: 'server',
  },
  {
    id: 'document-to-ps',
    title: 'documentToPs',
    description: 'serverRequiredDesc',
    accept: '.doc,.docx,.pdf,.ps,.txt,.text,.word',
    category: 'document',
    outputExtension: 'ps',
    outputMimeType: 'application/postscript',
    browserSupported: false,
    requiresServer: true,
    strategy: 'server',
  },
  {
    id: 'document-to-word',
    title: 'documentToWord',
    description: 'serverRequiredDesc',
    accept: '.doc,.docx,.pdf,.ps,.txt,.text,.word',
    category: 'document',
    outputExtension: 'word',
    outputMimeType: 'application/msword',
    browserSupported: false,
    requiresServer: true,
    strategy: 'server',
  },
  {
    id: 'json-to-csv',
    title: 'jsonToCsv',
    description: 'jsonToCsvDesc',
    accept: '.json,application/json',
    category: 'data',
    outputExtension: 'csv',
    outputMimeType: 'text/csv;charset=utf-8',
    browserSupported: true,
    strategy: 'json-csv',
  },
  {
    id: 'csv-to-json',
    title: 'csvToJson',
    description: 'csvToJsonDesc',
    accept: '.csv,text/csv',
    category: 'data',
    outputExtension: 'json',
    outputMimeType: 'application/json;charset=utf-8',
    browserSupported: true,
    strategy: 'csv-json',
  },
  {
    id: 'file-to-zip',
    title: 'textToZip',
    description: 'textToZipDesc',
    accept: '*',
    category: 'archive',
    outputExtension: 'zip',
    outputMimeType: 'application/zip',
    browserSupported: true,
    strategy: 'zip',
  },
];

export class FileConversionService {
  private readonly imageProcessor = new ImageProcessor();
  private readonly ffmpegTranscoder = new FfmpegTranscoder();
  private readonly profileMap = new Map(profiles.map((profile) => [profile.id, profile]));

  public readonly profiles: ConversionProfile[] = profiles;

  public getProfilesByCategory(): Array<{ category: ConversionCategory; profiles: ConversionProfile[] }> {
    const categories: ConversionCategory[] = ['image', 'audio', 'video', 'document', 'data', 'archive'];

    return categories.map((category) => ({
      category,
      profiles: this.profiles.filter((profile) => profile.category === category),
    }));
  }

  public getProfilesForFile(file: File): ConversionProfile[] {
    const categories = this.getSourceCategories(file);
    return this.profiles.filter((profile) => categories.includes(profile.category) || profile.accept === '*');
  }

  public getDefaultProfileForFile(file: File): ConversionProfileId {
    const extension = this.getFileExtension(file.name);
    const sourceCategory = this.getSourceCategories(file)[0];

    if (sourceCategory === 'audio') return 'audio-to-mp3';
    if (sourceCategory === 'video') return 'video-to-mp4';
    if (sourceCategory === 'image') return 'image-to-png';
    if (extension === 'json') return 'json-to-csv';
    if (extension === 'csv') return 'csv-to-json';
    if (extension === 'docx') return 'docx-to-txt';
    if (extension === 'txt' || extension === 'text') return 'txt-to-pdf';

    return 'file-to-zip';
  }

  public async convert(file: File, profileId: ConversionProfileId, options: ConversionOptions = { compression: 0.3 }): Promise<ProcessedFile> {
    const profile = this.profileMap.get(profileId);

    if (!profile) {
      throw new Error(`Unsupported conversion profile: ${profileId}`);
    }

    if (!profile.browserSupported && !profile.requiresServer) {
      throw new Error('This format needs a server conversion engine such as LibreOffice, Ghostscript or ImageMagick.');
    }

    switch (profile.strategy) {
      case 'canvas-image':
        return this.convertWithCanvas(file, profile, options);
      case 'ffmpeg-media':
        return this.ffmpegTranscoder.convert(file, profile, options);
      case 'raster-svg':
        return this.rasterImageToSvg(file);
      case 'json-csv':
        return this.jsonToCsv(file);
      case 'csv-json':
        return this.csvToJson(file);
      case 'zip':
        return this.fileToZip(file, options);
      case 'docx-text':
        return this.docxToText(file);
      case 'text-pdf':
        return this.textToPdf(file);
      case 'text-copy':
        return this.textCopy(file, profile.outputExtension);
      case 'server':
        return this.convertWithServer(file, profile, options);
      default:
        throw new Error(`Unsupported conversion strategy: ${profile.strategy}`);
    }
  }

  public estimateOutputSize(file: File, profileId: ConversionProfileId, options: ConversionOptions = { compression: 0.3 }): SizeEstimate | null {
    const profile = this.profileMap.get(profileId);
    if (!profile || file.size <= 0) {
      return null;
    }

    const sourceExtension = this.getFileExtension(file.name);
    const bytes = Math.round(file.size * getEstimatedSizeRatio(profile, sourceExtension, options));

    return {
      bytes: Math.max(128, bytes),
      confidence: getEstimateConfidence(profile, sourceExtension),
    };
  }

  private async convertWithCanvas(file: File, profile: ProfileDefinition, options: ConversionOptions): Promise<ProcessedFile> {
    if (!profile.canvasFormat) {
      throw new Error(`Canvas output format is not configured for ${profile.id}.`);
    }

    const processed = await this.imageProcessor.process(file, {
      outputFormat: profile.canvasFormat,
      quality: getImageQuality(options),
    });

    return {
      ...processed,
      fileName: this.getOutputName(file.name, profile.outputExtension),
    };
  }

  private async jsonToCsv(file: File): Promise<ProcessedFile> {
    const content = await file.text();
    const parsed = JSON.parse(content) as unknown;
    const rows = Array.isArray(parsed) ? parsed : [parsed];
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

    return this.toProcessedFile(blob, file.name, 'csv');
  }

  private async csvToJson(file: File): Promise<ProcessedFile> {
    const content = await file.text();
    const result = Papa.parse<Record<string, string>>(content, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
    });

    if (result.errors.length > 0) {
      throw new Error(result.errors[0].message);
    }

    const json = JSON.stringify(result.data, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });

    return this.toProcessedFile(blob, file.name, 'json');
  }

  private async fileToZip(file: File, options: ConversionOptions): Promise<ProcessedFile> {
    const buffer = await file.arrayBuffer();
    const zip = zipSync(
      {
        [file.name]: new Uint8Array(buffer),
        'README.txt': strToU8('Created locally by Kim Converter.'),
      },
      { level: getZipLevel(options) },
    );
    const blob = new Blob([zip], { type: 'application/zip' });

    return this.toProcessedFile(blob, file.name, 'zip');
  }

  private async docxToText(file: File): Promise<ProcessedFile> {
    const archive = unzipSync(new Uint8Array(await file.arrayBuffer()));
    const documentXml = archive['word/document.xml'];

    if (!documentXml) {
      throw new Error('DOCX document.xml was not found.');
    }

    const xml = strFromU8(documentXml);
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    const parseError = doc.querySelector('parsererror');

    if (parseError) {
      throw new Error('DOCX XML could not be parsed.');
    }

    const paragraphs = Array.from(doc.getElementsByTagName('w:p')).map((paragraph) =>
      Array.from(paragraph.getElementsByTagName('w:t'))
        .map((node) => node.textContent ?? '')
        .join(''),
    );
    const text = paragraphs.filter(Boolean).join('\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });

    return this.toProcessedFile(blob, file.name, 'txt');
  }

  private async textCopy(file: File, extension: string): Promise<ProcessedFile> {
    const blob = new Blob([await file.text()], { type: 'text/plain;charset=utf-8' });
    return this.toProcessedFile(blob, file.name, extension);
  }

  private async convertWithServer(file: File, profile: ProfileDefinition, options: ConversionOptions): Promise<ProcessedFile> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('profileId', profile.id);
    formData.append('compression', String(options.compression));

    let response: Response;

    try {
      response = await fetch('/api/convert', {
        method: 'POST',
        body: formData,
      });
    } catch (caught) {
      throw new Error(getServerNetworkErrorMessage(caught));
    }

    if (!response.ok) {
      const message = await getServerErrorMessage(response);
      throw new Error(message);
    }

    const blob = await response.blob();
    const fileName = getResponseFileName(response) ?? this.toProcessedFile(blob, file.name, profile.outputExtension).fileName;

    return {
      blob,
      fileName,
      mimeType: blob.type || profile.outputMimeType,
      size: blob.size,
    };
  }

  private async textToPdf(file: File): Promise<ProcessedFile> {
    const pdf = createSimplePdf(await file.text());
    const blob = new Blob([toArrayBuffer(pdf)], { type: 'application/pdf' });

    return this.toProcessedFile(blob, file.name, 'pdf');
  }

  private async rasterImageToSvg(file: File): Promise<ProcessedFile> {
    const dataUrl = await readAsDataUrl(file);
    const dimensions = await getImageDimensions(file).catch(() => ({ width: 1024, height: 768 }));
    const svg = [
      `<svg xmlns="http://www.w3.org/2000/svg" width="${dimensions.width}" height="${dimensions.height}" viewBox="0 0 ${dimensions.width} ${dimensions.height}">`,
      `<image href="${escapeXml(dataUrl)}" width="${dimensions.width}" height="${dimensions.height}" preserveAspectRatio="xMidYMid meet"/>`,
      '</svg>',
    ].join('');
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });

    return this.toProcessedFile(blob, file.name, 'svg');
  }

  private toProcessedFile(blob: Blob, inputName: string, extension: string): ProcessedFile {
    return {
      blob,
      fileName: this.getOutputName(inputName, extension),
      size: blob.size,
      mimeType: blob.type,
    };
  }

  private getOutputName(inputName: string, extension: string): string {
    const baseName = inputName.replace(/\.[^.]+$/, '') || 'converted';

    return `${baseName}.${extension}`;
  }

  private getFileExtension(fileName: string): string {
    const extension = fileName.split('.').pop();
    return extension ? extension.toLowerCase() : '';
  }

  private getSourceCategories(file: File): ConversionCategory[] {
    const extension = this.getFileExtension(file.name);

    if (file.type.startsWith('audio/')) return ['audio'];
    if (file.type.startsWith('video/')) return ['video'];
    if (extension === 'gif') return ['image', 'video'];
    if (
      file.type.startsWith('image/') ||
      this.matchesExtension(extension, ['bmp', 'eps', 'gif', 'ico', 'jpeg', 'jpg', 'odd', 'png', 'psd', 'svg', 'tga', 'tif', 'tiff', 'webp'])
    ) {
      return ['image'];
    }

    if (this.matchesExtension(extension, ['csv', 'json'])) return ['data'];
    if (this.matchesExtension(extension, ['doc', 'docx', 'pdf', 'ps', 'text', 'txt', 'word'])) return ['document'];

    return ['archive'];
  }

  private matchesExtension(extension: string, values: string[]): boolean {
    return values.includes(extension);
  }
}

class FfmpegTranscoder {
  private ffmpeg: import('@ffmpeg/ffmpeg').FFmpeg | null = null;
  private loading: Promise<void> | null = null;
  private queue: Promise<ProcessedFile | void> = Promise.resolve();

  public convert(file: File, profile: ProfileDefinition, options: ConversionOptions): Promise<ProcessedFile> {
    const task = async () => this.convertImmediately(file, profile, options);
    const result = this.queue.then(task, task);
    this.queue = result.then(
      () => undefined,
      () => undefined,
    );

    return result;
  }

  private async convertImmediately(file: File, profile: ProfileDefinition, options: ConversionOptions): Promise<ProcessedFile> {
    if (!profile.ffmpegArgs) {
      throw new Error(`FFmpeg arguments are not configured for ${profile.id}.`);
    }

    const ffmpeg = await this.getFfmpeg();
    const inputName = `input-${createId()}.${getFileExtension(file.name) || 'bin'}`;
    const outputName = `output-${createId()}.${profile.outputExtension}`;

    await ffmpeg.writeFile(inputName, new Uint8Array(await file.arrayBuffer()));

    try {
      const exitCode = await ffmpeg.exec(profile.ffmpegArgs(inputName, outputName, options), 120_000);

      if (exitCode !== 0) {
        throw new Error(`FFmpeg exited with code ${exitCode}.`);
      }

      const data = await ffmpeg.readFile(outputName);
      const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
      const blob = new Blob([toArrayBuffer(bytes)], { type: profile.outputMimeType });
      const baseName = file.name.replace(/\.[^.]+$/, '') || 'converted';

      return {
        blob,
        fileName: `${baseName}.${profile.outputExtension}`,
        mimeType: profile.outputMimeType,
        size: blob.size,
      };
    } finally {
      await this.deleteFileQuietly(ffmpeg, inputName);
      await this.deleteFileQuietly(ffmpeg, outputName);
    }
  }

  private async getFfmpeg(): Promise<import('@ffmpeg/ffmpeg').FFmpeg> {
    if (this.ffmpeg?.loaded) {
      return this.ffmpeg;
    }

    if (!this.loading) {
      this.loading = this.load();
    }

    await this.loading;

    if (!this.ffmpeg) {
      throw new Error('FFmpeg failed to initialize.');
    }

    return this.ffmpeg;
  }

  private async load(): Promise<void> {
    const [{ FFmpeg }, coreUrlModule, wasmUrlModule] = await Promise.all([
      import('@ffmpeg/ffmpeg'),
      import('@ffmpeg/core?url'),
      import('@ffmpeg/core/wasm?url'),
    ]);
    const ffmpeg = new FFmpeg();
    await ffmpeg.load({
      coreURL: coreUrlModule.default,
      wasmURL: wasmUrlModule.default,
    });
    this.ffmpeg = ffmpeg;
  }

  private async deleteFileQuietly(ffmpeg: import('@ffmpeg/ffmpeg').FFmpeg, path: string): Promise<void> {
    try {
      await ffmpeg.deleteFile(path);
    } catch {
      // File may not exist when conversion fails before output creation.
    }
  }
}

function createSimplePdf(text: string): Uint8Array {
  const lines = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .flatMap((line) => splitLongLine(line, 88));
  const visibleLines = lines.length > 0 ? lines : [''];
  const content = [
    'BT',
    '/F1 10 Tf',
    '50 790 Td',
    '14 TL',
    ...visibleLines.slice(0, 52).map((line, index) => `${index === 0 ? '' : 'T*'}(${escapePdfString(line)}) Tj`),
    'ET',
  ].join('\n');
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
  ];
  const parts = ['%PDF-1.4\n'];
  const offsets = [0];

  objects.forEach((object, index) => {
    offsets.push(parts.join('').length);
    parts.push(`${index + 1} 0 obj\n${object}\nendobj\n`);
  });

  const xrefOffset = parts.join('').length;
  parts.push(`xref\n0 ${objects.length + 1}\n`);
  parts.push('0000000000 65535 f \n');
  offsets.slice(1).forEach((offset) => {
    parts.push(`${String(offset).padStart(10, '0')} 00000 n \n`);
  });
  parts.push(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return new TextEncoder().encode(parts.join(''));
}

function splitLongLine(line: string, maxLength: number): string[] {
  if (line.length <= maxLength) {
    return [line];
  }

  const result: string[] = [];

  for (let index = 0; index < line.length; index += maxLength) {
    result.push(line.slice(index, index + maxLength));
  }

  return result;
}

function getImageQuality(options: ConversionOptions): number {
  return clamp(0.25, 0.95 - options.compression * 0.7, 0.95);
}

function getAudioBitrate(options: ConversionOptions): number {
  return Math.round(clamp(96, 320 - options.compression * 224, 320));
}

function getVideoCrf(options: ConversionOptions): number {
  return Math.round(clamp(18, 18 + options.compression * 18, 36));
}

function getVideoBitrate(options: ConversionOptions): number {
  return Math.round(clamp(800, 3200 - options.compression * 2400, 3200));
}

function getGifFps(options: ConversionOptions): number {
  return Math.round(clamp(8, 18 - options.compression * 10, 18));
}

function getGifWidth(options: ConversionOptions): number {
  return Math.round(clamp(420, 900 - options.compression * 480, 900));
}

function getZipLevel(options: ConversionOptions): 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 {
  return Math.round(clamp(1, 1 + options.compression * 8, 9)) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
}

function getEstimatedSizeRatio(profile: ProfileDefinition, sourceExtension: string, options: ConversionOptions): number {
  const compression = clamp(0, options.compression, 1);
  const targetExtension = profile.outputExtension.toLowerCase();

  if (profile.strategy === 'zip') {
    return isCompressedSource(sourceExtension) ? 1.03 : clamp(0.22, 0.82 - compression * 0.42, 0.92);
  }

  if (profile.category === 'image') {
    const baseRatioByTarget: Record<string, number> = {
      avif: 0.32,
      webp: 0.42,
      jpg: 0.58,
      jpeg: 0.58,
      png: 1.08,
      gif: 0.9,
      bmp: 3.8,
      ico: 0.38,
      svg: 1.12,
      tga: 3.2,
      tiff: 2.2,
      eps: 1.35,
      psd: 2.4,
      odd: 1.08,
    };
    const sourceAdjustment = getImageSourceAdjustment(sourceExtension, targetExtension);
    return clamp(0.08, (baseRatioByTarget[targetExtension] ?? 0.85) * sourceAdjustment * (1 - compression * 0.42), 5.2);
  }

  if (profile.category === 'audio') {
    const baseRatioByTarget: Record<string, number> = {
      mp3: 0.7,
      aac: 0.62,
      m4a: 0.64,
      ogg: 0.58,
      flac: 0.82,
      wav: 3.4,
    };
    return clamp(0.12, (baseRatioByTarget[targetExtension] ?? 0.85) * (1 - compression * 0.28), 4);
  }

  if (profile.category === 'video') {
    const baseRatioByTarget: Record<string, number> = {
      mp4: 0.72,
      webm: 0.64,
      gif: 2.8,
    };
    return clamp(0.16, (baseRatioByTarget[targetExtension] ?? 0.9) * (1 - compression * 0.36), 5.5);
  }

  if (profile.category === 'document') {
    const baseRatioByTarget: Record<string, number> = {
      txt: 0.18,
      text: 1,
      pdf: 1.28,
      doc: 1.1,
      docx: 0.92,
      ps: 1.55,
      word: 1.1,
    };
    return clamp(0.08, baseRatioByTarget[targetExtension] ?? 1, 2.8);
  }

  if (profile.category === 'data') {
    return targetExtension === 'json' ? 1.35 : 0.76;
  }

  return 1;
}

function getImageSourceAdjustment(sourceExtension: string, targetExtension: string): number {
  if (!sourceExtension || sourceExtension === targetExtension) {
    return 1;
  }

  if (['jpg', 'jpeg', 'webp', 'avif'].includes(sourceExtension) && targetExtension === 'png') {
    return 2.4;
  }

  if (sourceExtension === 'png' && ['jpg', 'jpeg', 'webp', 'avif'].includes(targetExtension)) {
    return 0.72;
  }

  if (['bmp', 'tga', 'tiff'].includes(sourceExtension)) {
    return 0.42;
  }

  if (sourceExtension === 'gif' && ['mp4', 'webm'].includes(targetExtension)) {
    return 0.55;
  }

  return 1;
}

function getEstimateConfidence(profile: ProfileDefinition, sourceExtension: string): SizeEstimate['confidence'] {
  if (profile.category === 'data' || profile.strategy === 'text-copy') {
    return 'high';
  }

  if (profile.strategy === 'zip' || profile.category === 'document' || profile.requiresServer || sourceExtension === 'gif') {
    return 'low';
  }

  return 'medium';
}

function isCompressedSource(extension: string): boolean {
  return ['7z', 'aac', 'avif', 'flac', 'gif', 'gz', 'jpg', 'jpeg', 'm4a', 'mp3', 'mp4', 'ogg', 'png', 'rar', 'webm', 'webp', 'zip'].includes(extension);
}

function clamp(min: number, value: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function escapePdfString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

function escapeXml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(String(reader.result)));
    reader.addEventListener('error', () => reject(reader.error ?? new Error('Failed to read file.')));
    reader.readAsDataURL(file);
  });
}

async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const dimensions = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return dimensions;
}

function getFileExtension(fileName: string): string {
  const extension = fileName.split('.').pop();
  return extension ? extension.toLowerCase() : '';
}

function createId(): string {
  return globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function getServerErrorMessage(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    const payload = (await response.json().catch(() => null)) as { error?: string; maxFileSize?: number } | null;
    if (payload?.error) {
      return payload.error;
    }
  }

  const text = await response.text().catch(() => '');
  return normalizeServerErrorText(text, response.status);
}

function getServerNetworkErrorMessage(caught: unknown): string {
  if (caught instanceof TypeError && /fetch/i.test(caught.message)) {
    return 'Server conversion API is unavailable. Locally, start npm run server alongside npm run dev. In production, check nginx client_max_body_size and the API service status.';
  }

  return caught instanceof Error ? caught.message : 'Server conversion API is unavailable.';
}

function normalizeServerErrorText(text: string, status: number): string {
  const normalized = text
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (status === 413 || /413 request entity too large/i.test(normalized)) {
    return 'The file is too large for server conversion. Increase nginx client_max_body_size and CONVERSION_MAX_FILE_SIZE, or use a smaller file.';
  }

  return normalized.slice(0, 240) || `Server conversion failed with status ${status}.`;
}

function getResponseFileName(response: Response): string | null {
  const disposition = response.headers.get('content-disposition');
  if (!disposition) {
    return null;
  }

  const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1]);
  }

  const quotedMatch = /filename="([^"]+)"/i.exec(disposition);
  return quotedMatch?.[1] ?? null;
}
